import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import superAdminApi from '../../utils/superAdminApi';
import toast from 'react-hot-toast';
import {
  FiCalendar, FiTrendingUp, FiHome, FiPackage, FiDownload, FiMail,
  FiSun, FiAlertTriangle, FiCreditCard, FiShoppingCart, FiTruck, FiEye,
} from 'react-icons/fi';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'daily', label: 'Daily' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'range', label: 'Date Range' },
  { key: 'shopwise', label: 'Shop-wise' },
  { key: 'products', label: 'Products' },
  { key: 'inventory', label: 'Inventory' },
];

const PERIODS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'all', label: 'All Time' },
];

// Saudi wall-clock "now" (UTC+3, no DST) as a Date whose UTC fields are the
// Saudi date/time — so "today" is the Saudi day whatever the browser's zone.
const saudiNow = () => new Date(Date.now() + 3 * 60 * 60 * 1000);
const todayStr = () => saudiNow().toISOString().split('T')[0];

// Sunday-based week start, matching the backend's moment().startOf('week')
// (moment's default locale) — same convention the app's Home screen uses.
const weekStartStr = () => {
  const d = saudiNow();
  d.setUTCDate(d.getUTCDate() - d.getUTCDay());
  return d.toISOString().split('T')[0];
};

export default function SuperAdminReports() {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState('');
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(false);

  const [orderStatsPeriod, setOrderStatsPeriod] = useState('week');
  const [orderStats, setOrderStats] = useState(null);
  const [exportingOverview, setExportingOverview] = useState(false);

  // Drill-down modal — clicking an Overview card opens the same underlying
  // order list the app Home screen's card tap opens, filtered the same way.
  const [drillDown, setDrillDown] = useState(null); // { title, filters }
  const [drillBills, setDrillBills] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);

  const [dailyDate, setDailyDate] = useState(todayStr());
  const [dailyReport, setDailyReport] = useState(null);

  const [year, setYear] = useState(new Date().getFullYear());
  const [monthlyReport, setMonthlyReport] = useState(null);

  const [startDate, setStartDate] = useState(todayStr());
  const [endDate, setEndDate] = useState(todayStr());
  const [rangeReport, setRangeReport] = useState(null);

  const [shopWiseReport, setShopWiseReport] = useState(null);
  const [productsReport, setProductsReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);

  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailing, setEmailing] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    superAdminApi.get('/shops').then(r => {
      setShops(r.data.shops);
      if (r.data.shops.length > 0) setShopId(r.data.shops[0]._id);
    }).catch(() => toast.error('Failed to load shops'));
  }, []);

  const fetchOrderStats = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const r = await superAdminApi.get('/reports/order-stats', { params: { shopId, period: orderStatsPeriod } });
      setOrderStats(r.data.stats);
    } catch { toast.error('Failed to load overview'); }
    setLoading(false);
  }, [shopId, orderStatsPeriod]);

  const fetchDaily = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const r = await superAdminApi.get('/reports/daily', { params: { shopId, date: dailyDate } });
      setDailyReport(r.data.report);
    } catch { toast.error('Failed to load daily report'); }
    setLoading(false);
  }, [shopId, dailyDate]);

  const fetchMonthly = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const r = await superAdminApi.get('/reports/monthly', { params: { shopId, year } });
      setMonthlyReport(r.data.report);
    } catch { toast.error('Failed to load monthly report'); }
    setLoading(false);
  }, [shopId, year]);

  const fetchRange = useCallback(async () => {
    if (!shopId || !startDate || !endDate) return;
    setLoading(true);
    try {
      const r = await superAdminApi.get('/reports/range', { params: { shopId, startDate, endDate } });
      setRangeReport(r.data.report);
    } catch { toast.error('Failed to load range report'); }
    setLoading(false);
  }, [shopId, startDate, endDate]);

  const fetchShopWise = useCallback(async () => {
    setLoading(true);
    try {
      const r = await superAdminApi.get('/reports/shop-wise');
      setShopWiseReport(r.data.report);
    } catch { toast.error('Failed to load shop-wise report'); }
    setLoading(false);
  }, []);

  const fetchProducts = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const r = await superAdminApi.get('/reports/product-wise', { params: { shopId } });
      setProductsReport(r.data.report);
    } catch { toast.error('Failed to load product report'); }
    setLoading(false);
  }, [shopId]);

  const fetchInventory = useCallback(async () => {
    if (!shopId) return;
    setLoading(true);
    try {
      const r = await superAdminApi.get('/reports/inventory', { params: { shopId } });
      setInventoryReport(r.data.report);
    } catch { toast.error('Failed to load inventory report'); }
    setLoading(false);
  }, [shopId]);

  useEffect(() => {
    if (tab === 'overview') fetchOrderStats();
    else if (tab === 'daily') fetchDaily();
    else if (tab === 'monthly') fetchMonthly();
    else if (tab === 'range') fetchRange();
    else if (tab === 'shopwise') fetchShopWise();
    else if (tab === 'products') fetchProducts();
    else if (tab === 'inventory') fetchInventory();
  }, [tab, fetchOrderStats, fetchDaily, fetchMonthly, fetchRange, fetchShopWise, fetchProducts, fetchInventory]);

  // Maps the Overview period chip to a concrete date range for PDF export,
  // same convention as the app Home screen's period filter.
  const exportOverviewPDF = async () => {
    if (!shopId) return toast.error('Pick a shop first');
    const end = todayStr();
    const start = orderStatsPeriod === 'today' ? todayStr()
      : orderStatsPeriod === 'week' ? weekStartStr()
      : '2020-01-01';
    setExportingOverview(true);
    try {
      const res = await superAdminApi.get('/reports/export/pdf', {
        params: { shopId, startDate: start, endDate: end }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Report_${start}_to_${end}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
    setExportingOverview(false);
  };

  // Same filter shapes the app Home screen sends to GET /api/bills on a
  // card tap (see shalwah/lib/screens/home_screen.dart), rebuilt here for
  // GET /api/superadmin/sales, keyed to the current Overview period.
  const cardFilters = () => {
    const now = saudiNow();
    const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000 - 1000);
    const fmt = (d) => d.toISOString().split('T')[0];
    const periodStart = orderStatsPeriod === 'all' ? new Date(Date.UTC(2020, 0, 1))
      : orderStatsPeriod === 'today' ? todayStart
      : new Date(todayStart.getTime() - todayStart.getUTCDay() * 24 * 60 * 60 * 1000);
    return {
      todayDue: { isDelivered: 'false', deliveryFrom: fmt(todayStart), deliveryTo: fmt(todayEnd) },
      pastDue: { isDelivered: 'false', deliveryTo: fmt(new Date(todayStart.getTime() - 1000)) },
      pendingPayment: { remainingGt0: 'true' },
      newOrders: { startDate: fmt(periodStart) },
      delivered: orderStatsPeriod === 'all'
        ? { isDelivered: 'true' }
        : { isDelivered: 'true', deliveredFrom: fmt(periodStart) },
    };
  };

  const openDrillDown = async (title, filters) => {
    if (!shopId) return toast.error('Pick a shop first');
    setDrillDown({ title, filters });
    setDrillLoading(true);
    try {
      const r = await superAdminApi.get('/sales', { params: { shopId, limit: 200, ...filters } });
      setDrillBills(r.data.bills);
    } catch { toast.error('Failed to load orders'); }
    setDrillLoading(false);
  };

  const statusBadge = (status) => {
    const map = { paid: 'badge-success', pending: 'badge-warning', partial: 'badge-info' };
    return <span className={`badge ${map[status] || 'badge-gray'}`}>{status}</span>;
  };

  const exportPDF = async () => {
    if (!shopId || !startDate || !endDate) return toast.error('Pick a shop and date range first');
    setExporting(true);
    try {
      const res = await superAdminApi.get('/reports/export/pdf', {
        params: { shopId, startDate, endDate }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Report_${startDate}_to_${endDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
    setExporting(false);
  };

  const emailReport = async () => {
    if (!emailTo) return toast.error('Enter an email address');
    setEmailing(true);
    try {
      await superAdminApi.post('/reports/email', { shopId, startDate, endDate, to: emailTo });
      toast.success('Report emailed successfully');
      setShowEmailModal(false);
      setEmailTo('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Email failed');
    }
    setEmailing(false);
  };

  const shopSelector = tab !== 'shopwise' && (
    <select className="form-control" style={{ width: 220 }} value={shopId} onChange={e => setShopId(e.target.value)}>
      {shops.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
    </select>
  );

  return (
    <div>
      <div className="filter-tabs" style={{ marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t.key} className={`filter-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {/* OVERVIEW — same 5 cards as the shop app's own Home screen (Today
          Delivery Due, Past Due, Pending Payment, New Orders, Delivered),
          scoped to whichever shop is selected. */}
      {tab === 'overview' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                {shopSelector}
                <div className="filter-tabs" style={{ margin: 0 }}>
                  {PERIODS.map(p => (
                    <button key={p.key} className={`filter-tab ${orderStatsPeriod === p.key ? 'active' : ''}`}
                      onClick={() => setOrderStatsPeriod(p.key)}>{p.label}</button>
                  ))}
                </div>
              </div>
              <button className="btn btn-outline" onClick={exportOverviewPDF} disabled={exportingOverview || !shopId}>
                <FiDownload size={14} /> {exportingOverview ? 'Exporting...' : 'Download PDF'}
              </button>
            </div>
          </div>

          {loading ? <div className="loading"><div className="spinner"></div></div> : orderStats && (() => {
            const f = cardFilters();
            return (
              <div className="stats-grid">
                <div className="stat-card" style={{ cursor: 'pointer' }}
                  onClick={() => openDrillDown('Today Delivery Due', f.todayDue)}>
                  <div className="stat-value"><FiSun size={16} color="#3b82f6" style={{ marginRight: 4, verticalAlign: -2 }} />{orderStats.todayDue.count}</div>
                  <div className="stat-label">Today Delivery Due</div>
                </div>
                <div className="stat-card" style={{ cursor: 'pointer' }}
                  onClick={() => openDrillDown('Past Due', f.pastDue)}>
                  <div className="stat-value" style={{ color: '#ef4444' }}>
                    <FiAlertTriangle size={16} style={{ marginRight: 4, verticalAlign: -2 }} />{orderStats.pastDue.count}
                  </div>
                  <div className="stat-label">Past Due</div>
                </div>
                <div className="stat-card" style={{ cursor: 'pointer' }}
                  onClick={() => openDrillDown('Pending Payment', f.pendingPayment)}>
                  <div className="stat-value" style={{ color: '#f59e0b' }}>
                    <FiCreditCard size={16} style={{ marginRight: 4, verticalAlign: -2 }} />{orderStats.pendingPayment.count}
                  </div>
                  <div className="stat-label">Pending Payment</div>
                  <div style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600, marginTop: 2 }}>
                    SAR {orderStats.pendingPayment.amount.toFixed(0)}
                  </div>
                </div>
                <div className="stat-card" style={{ cursor: 'pointer' }}
                  onClick={() => openDrillDown('New Orders', f.newOrders)}>
                  <div className="stat-value" style={{ color: '#8b5cf6' }}>
                    <FiShoppingCart size={16} style={{ marginRight: 4, verticalAlign: -2 }} />{orderStats.newOrders.count}
                  </div>
                  <div className="stat-label">New Orders</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    Sales: SAR {orderStats.newOrders.sales.toFixed(0)} · Items: {orderStats.newOrders.items}
                  </div>
                </div>
                <div className="stat-card" style={{ cursor: 'pointer' }}
                  onClick={() => openDrillDown('Delivered', f.delivered)}>
                  <div className="stat-value" style={{ color: '#10b981' }}>
                    <FiTruck size={16} style={{ marginRight: 4, verticalAlign: -2 }} />{orderStats.delivered.count}
                  </div>
                  <div className="stat-label">Delivered</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    Items: {orderStats.delivered.items} · SAR {orderStats.delivered.price.toFixed(0)}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* DAILY */}
      {tab === 'daily' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              {shopSelector}
              <FiCalendar size={16} color="#94a3b8" />
              <input type="date" className="form-control" style={{ width: 200 }}
                value={dailyDate} onChange={e => setDailyDate(e.target.value)} />
            </div>
          </div>

          {loading ? <div className="loading"><div className="spinner"></div></div> : dailyReport && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">{dailyReport.billsCount}</div>
                  <div className="stat-label">Bills Created</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">SAR {dailyReport.totalRevenue.toFixed(0)}</div>
                  <div className="stat-label">Total Revenue ({dailyReport.date})</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: '#10b981' }}>SAR {dailyReport.totalCollected.toFixed(0)}</div>
                  <div className="stat-label">Collected</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: '#ef4444' }}>SAR {dailyReport.totalPending.toFixed(0)}</div>
                  <div className="stat-label">Pending</div>
                </div>
              </div>

              <div className="card">
                <div className="card-header"><h3 className="card-title">Bills on {dailyReport.date}</h3></div>
                <div className="table-container">
                  <table>
                    <thead><tr><th>Bill #</th><th>Customer</th><th>Total</th><th>Advance</th><th>Remaining</th><th>Status</th></tr></thead>
                    <tbody>
                      {dailyReport.bills.length === 0 && (
                        <tr><td colSpan={6}><div className="empty-state"><h3>No bills on this date</h3></div></td></tr>
                      )}
                      {dailyReport.bills.map(b => (
                        <tr key={b._id}>
                          <td style={{ fontWeight: 600 }}>#{b.billNumber}</td>
                          <td>{b.customerName}</td>
                          <td>SAR {b.total.toFixed(2)}</td>
                          <td>SAR {b.advancePaid.toFixed(2)}</td>
                          <td style={{ color: b.remaining > 0 ? '#ef4444' : '#10b981' }}>SAR {b.remaining.toFixed(2)}</td>
                          <td>{statusBadge(b.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* MONTHLY */}
      {tab === 'monthly' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              {shopSelector}
              <FiTrendingUp size={16} color="#94a3b8" />
              <label className="form-label" style={{ marginBottom: 0 }}>Year</label>
              <input type="number" className="form-control" style={{ width: 120 }}
                value={year} onChange={e => setYear(e.target.value)} />
            </div>
          </div>
          {loading ? <div className="loading"><div className="spinner"></div></div> : monthlyReport && (
            <div className="card">
              <div className="card-header"><h3 className="card-title">Monthly Revenue — {year}</h3></div>
              <div className="table-container">
                <table>
                  <thead><tr><th>Month</th><th>Bills</th><th>Revenue</th><th>Collected</th><th>Pending</th></tr></thead>
                  <tbody>
                    {monthlyReport.map(m => (
                      <tr key={m.month}>
                        <td style={{ fontWeight: 600 }}>{m.month}</td>
                        <td>{m.billsCount}</td>
                        <td>SAR {m.totalRevenue.toFixed(2)}</td>
                        <td style={{ color: '#10b981' }}>SAR {m.totalCollected.toFixed(2)}</td>
                        <td style={{ color: m.totalPending > 0 ? '#ef4444' : '#10b981' }}>SAR {m.totalPending.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* RANGE */}
      {tab === 'range' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              {shopSelector}
              <FiCalendar size={16} color="#94a3b8" />
              <input type="date" className="form-control" style={{ width: 180 }}
                value={startDate} onChange={e => setStartDate(e.target.value)} />
              <span style={{ color: '#94a3b8' }}>to</span>
              <input type="date" className="form-control" style={{ width: 180 }}
                value={endDate} onChange={e => setEndDate(e.target.value)} />
              <button className="btn btn-accent" onClick={fetchRange}>Apply</button>
              <button className="btn btn-outline" onClick={exportPDF} disabled={exporting}>
                <FiDownload size={14} /> {exporting ? 'Exporting...' : 'Export PDF'}
              </button>
              <button className="btn btn-outline" onClick={() => setShowEmailModal(true)}>
                <FiMail size={14} /> Email Report
              </button>
            </div>
          </div>

          {loading ? <div className="loading"><div className="spinner"></div></div> : rangeReport && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">{rangeReport.billsCount}</div>
                  <div className="stat-label">Bills ({rangeReport.startDate} - {rangeReport.endDate})</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">SAR {rangeReport.totalRevenue.toFixed(0)}</div>
                  <div className="stat-label">Total Revenue</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: '#10b981' }}>SAR {rangeReport.totalCollected.toFixed(0)}</div>
                  <div className="stat-label">Collected</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: '#ef4444' }}>SAR {rangeReport.totalPending.toFixed(0)}</div>
                  <div className="stat-label">Pending</div>
                </div>
              </div>
              <div className="card">
                <div className="card-header"><h3 className="card-title">Bills</h3></div>
                <div className="table-container">
                  <table>
                    <thead><tr><th>Bill #</th><th>Customer</th><th>Date</th><th>Total</th><th>Remaining</th><th>Status</th></tr></thead>
                    <tbody>
                      {rangeReport.bills.length === 0 && (
                        <tr><td colSpan={6}><div className="empty-state"><h3>No bills in this range</h3></div></td></tr>
                      )}
                      {rangeReport.bills.map(b => (
                        <tr key={b._id}>
                          <td style={{ fontWeight: 600 }}>#{b.billNumber}</td>
                          <td>{b.customerName || b.customer?.name}</td>
                          <td>{new Date(b.billDate).toLocaleDateString('en-AE', { timeZone: 'Asia/Riyadh', day: 'numeric', month: 'short', year: 'numeric' })}</td>
                          <td>SAR {b.total.toFixed(2)}</td>
                          <td style={{ color: b.remaining > 0 ? '#ef4444' : '#10b981' }}>SAR {b.remaining.toFixed(2)}</td>
                          <td>{statusBadge(b.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* SHOP-WISE */}
      {tab === 'shopwise' && (
        <div>
          {loading ? <div className="loading"><div className="spinner"></div></div> : shopWiseReport && (
            <div className="card">
              <div className="card-header"><h3 className="card-title"><FiHome size={15} /> Revenue by Shop</h3></div>
              <div className="table-container">
                <table>
                  <thead><tr><th>Shop</th><th>Bills</th><th>Revenue</th></tr></thead>
                  <tbody>
                    {shopWiseReport.length === 0 && (
                      <tr><td colSpan={3}><div className="empty-state"><h3>No shops yet</h3></div></td></tr>
                    )}
                    {shopWiseReport.map(s => (
                      <tr key={s.shopId}>
                        <td style={{ fontWeight: 600 }}>{s.shopName}</td>
                        <td>{s.billsCount}</td>
                        <td>SAR {s.revenue.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PRODUCTS */}
      {tab === 'products' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>{shopSelector}</div>
          </div>
          {loading ? <div className="loading"><div className="spinner"></div></div> : productsReport && (
            <div className="card">
              <div className="card-header"><h3 className="card-title">Product Sales Summary</h3></div>
              <div className="table-container">
                <table>
                  <thead><tr><th>Product</th><th>Units Sold</th><th>Revenue</th></tr></thead>
                  <tbody>
                    {productsReport.length === 0 && (
                      <tr><td colSpan={3}><div className="empty-state"><h3>No product sales yet</h3></div></td></tr>
                    )}
                    {productsReport.map((p, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600 }}>{p.name}</td>
                        <td>{p.qty}</td>
                        <td>SAR {p.revenue.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* INVENTORY */}
      {tab === 'inventory' && (
        <div>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-body" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>{shopSelector}</div>
          </div>
          {loading ? <div className="loading"><div className="spinner"></div></div> : inventoryReport && (
            <>
              {inventoryReport.lowStock.length > 0 && (
                <div className="alert alert-danger" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                  <FiPackage size={16} />
                  <span><strong>{inventoryReport.lowStock.length}</strong> product(s) low on stock</span>
                </div>
              )}
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">{inventoryReport.totalProducts}</div>
                  <div className="stat-label">Total Products</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{inventoryReport.totalStockUnits}</div>
                  <div className="stat-label">Total Stock Units</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value" style={{ color: '#ef4444' }}>{inventoryReport.outOfStock.length}</div>
                  <div className="stat-label">Out of Stock</div>
                </div>
              </div>
              <div className="card">
                <div className="card-header"><h3 className="card-title">Products</h3></div>
                <div className="table-container">
                  <table>
                    <thead><tr><th>Product</th><th>Category</th><th>Price</th><th>Stock</th></tr></thead>
                    <tbody>
                      {inventoryReport.products.map(p => (
                        <tr key={p._id}>
                          <td style={{ fontWeight: 600 }}>{p.name}</td>
                          <td><span className="badge badge-gray">{p.category}</span></td>
                          <td>SAR {p.price?.toFixed(2)}</td>
                          <td style={{ color: p.stockQty <= p.minAlertQty ? '#ef4444' : '#10b981', fontWeight: 600 }}>{p.stockQty}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {drillDown && (
        <div className="modal-overlay" onClick={() => setDrillDown(null)}>
          <div className="modal" style={{ maxWidth: 800 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{drillDown.title} — {shops.find(s => s._id === shopId)?.name}</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setDrillDown(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {drillLoading ? <div className="loading"><div className="spinner"></div></div> : (
                <div className="table-container">
                  <table>
                    <thead>
                      <tr><th>Bill #</th><th>Customer</th><th>Delivery</th><th>Total</th><th>Remaining</th><th>Status</th><th></th></tr>
                    </thead>
                    <tbody>
                      {drillBills.length === 0 && (
                        <tr><td colSpan={7}><div className="empty-state"><h3>No orders found</h3></div></td></tr>
                      )}
                      {drillBills.map(b => (
                        <tr key={b._id}>
                          <td style={{ fontWeight: 600 }}>#{b.billNumber}</td>
                          <td>
                            <div style={{ fontWeight: 600 }}>{b.customerName}</div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{b.customerPhone}</div>
                          </td>
                          <td>{b.deliveryDate ? new Date(b.deliveryDate).toLocaleDateString('en-AE', { timeZone: 'Asia/Riyadh', day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                          <td>SAR {b.total?.toFixed(0)}</td>
                          <td style={{ color: b.remaining > 0 ? '#ef4444' : '#10b981', fontWeight: 600 }}>SAR {b.remaining?.toFixed(0)}</td>
                          <td>{statusBadge(b.status)}</td>
                          <td>
                            <button className="btn btn-outline btn-sm btn-icon" title="View"
                              onClick={() => navigate(`/superadmin/sales/${b._id}`)}>
                              <FiEye size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showEmailModal && (
        <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">📧 Email Report</h3>
              <button className="btn btn-outline btn-sm btn-icon" onClick={() => setShowEmailModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
                Sends the {startDate} to {endDate} report for the selected shop.
              </div>
              <div className="form-group">
                <label className="form-label">Recipient Email</label>
                <input type="email" className="form-control" placeholder="owner@example.com"
                  value={emailTo} onChange={e => setEmailTo(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowEmailModal(false)}>Cancel</button>
              <button className="btn btn-accent" onClick={emailReport} disabled={emailing}>
                {emailing ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
